<script setup lang="ts">
  import { computed, onMounted, reactive } from 'vue';
  import { ColoredButton, DatePicker, IconImage, SelectInput, UiModalHeader } from '@/shared/ui';
  import { formatDateToPtBR, useCustomToast } from '@/shared/lib';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import {
    RECORD_HOMOLOGATION_DECISION_STAGES,
    RECORD_HOMOLOGATION_ELABORATION_LABEL,
    useRecordHomologation,
    type RecordHomologationDecisionKey,
    type RecordHomologationStageKey,
  } from '../model/useRecordHomologation';
  import type { RecordModalTarget } from '@/widgets/record-modals';

  /**
   * A component of its own instead of the Gestão de Documentos one, which carries
   * the training and observations sections this design does not have and patches a
   * document revision. Same pattern, record endpoints, and members throughout.
   */
  const props = defineProps<{
    target: RecordModalTarget;
  }>();

  const emit = defineEmits<{
    close: [];
    saved: [];
  }>();

  const toast = useCustomToast();

  const {
    header,
    stages,
    missingStages,
    effectiveDateAsDate,
    error,
    load,
    save,
    memberOptions,
    positionOptions,
    setMemberId,
    setPositionId,
    stageDate,
    setStageDate,
    setEffectiveDate,
    effectiveDateError,
    canSave,
  } = useRecordHomologation();

  /** Component state, not form state: an open dropdown is not something to send. */
  const openPickers = reactive<Record<string, boolean>>({});

  onMounted(async () => {
    try {
      await load(props.target.id);
    } catch {
      toast.showErrorToast({
        title: 'Erro',
        message: error.value ?? 'Erro ao carregar a homologação',
        type: 'error',
      });
    }
  });

  /** Falls back to the row that opened the modal, so nothing blinks while loading. */
  const readOnlyFields = computed(() => [
    { key: 'code', label: 'Registro:', value: header.code || props.target.code },
    {
      key: 'title',
      label: 'Título:',
      value: header.title ?? props.target.title ?? '-',
    },
    {
      key: 'revision',
      label: 'Rev.:',
      value: String(header.revision ?? props.target.revision).padStart(2, '0'),
    },
  ]);

  const dateLabel = (date: Date | null) => formatDateToPtBR(date) || 'Selecione uma data';

  const toOptionalId = (value: string | number): number | undefined => {
    const parsed = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  const onMemberChange = (key: RecordHomologationStageKey, value: string | number) => {
    setMemberId(key, toOptionalId(value));
  };

  const onPositionChange = (key: RecordHomologationDecisionKey, value: string | number) => {
    setPositionId(key, toOptionalId(value));
  };

  const handleSave = async () => {
    try {
      await save(props.target.id);
      toast.showSuccessToast({
        title: 'Sucesso',
        message: 'Edição salva com sucesso',
        type: 'success',
      });
      emit('saved');
      emit('close');
    } catch {
      toast.showErrorToast({
        title: 'Erro',
        message: error.value ?? 'Erro ao salvar registro. Tente novamente',
        type: 'error',
      });
    }
  };
</script>

<template>
  <div class="flex max-h-[90vh] w-[79rem] flex-col rounded-2xl border border-gray-02 bg-white-01">
    <UiModalHeader
      class="shrink-0 border-b border-gray-02 px-6 py-3 text-sm"
      title="Editar Homologação"
      text-color="gray-07"
      title-size="text-sm"
      @close="emit('close')"
    />

    <div class="flex min-h-0 flex-1 flex-col divide-y divide-gray-02 overflow-auto">
      <div class="flex flex-wrap items-start gap-6 p-6">
        <div
          v-for="field in readOnlyFields"
          :key="field.key"
          class="flex flex-col gap-2"
        >
          <span class="text-sm font-semibold text-gray-07">{{ field.label }}</span>
          <div
            class="flex h-12 items-center rounded-lg border border-gray-02 bg-gray-02/20 px-5 text-sm text-gray-07"
            :data-testid="`record-homologation-${field.key}`"
          >
            {{ field.value }}
          </div>
        </div>
      </div>

      <!-- Outside the loop below because it has no Cargo: no column exists for one. -->
      <section
        class="flex flex-col gap-2 p-6"
        data-testid="record-homologation-stage-elaboration"
      >
        <h2 class="text-[0.9375rem] font-semibold text-gray-07">
          {{ RECORD_HOMOLOGATION_ELABORATION_LABEL }}
        </h2>
        <div class="flex flex-wrap items-start gap-6">
          <div class="flex w-80 flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Responsável:</span>
            <SelectInput
              :model-value="stages.elaboration.memberId ?? ''"
              :options="memberOptions('elaboration')"
              label="Selecione o responsável"
              data-testid="record-homologation-responsible-elaboration"
              @update:model-value="onMemberChange('elaboration', $event)"
            />
          </div>
          <div class="flex flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Data:</span>
            <button
              type="button"
              class="flex h-11 items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4 text-sm text-gray-07 shadow-xs disabled:opacity-50"
              data-testid="record-homologation-date-elaboration"
              @click="openPickers.elaboration = true"
            >
              <IconImage
                :src="CalendarIcon"
                alt=""
              />
              {{ dateLabel(stageDate('elaboration')) }}
            </button>
            <DatePicker
              :selected-date="stageDate('elaboration')"
              :is-open="openPickers.elaboration ?? false"
              @update:selected-date="setStageDate('elaboration', $event)"
              @update:is-open="openPickers.elaboration = $event"
            />
          </div>
        </div>
      </section>

      <section
        v-for="stage in RECORD_HOMOLOGATION_DECISION_STAGES"
        :key="stage.key"
        class="flex flex-col gap-2 p-6"
        :data-testid="`record-homologation-stage-${stage.key}`"
      >
        <h2 class="text-[0.9375rem] font-semibold text-gray-07">
          {{ stage.label }}
        </h2>
        <div class="flex flex-wrap items-start gap-6">
          <div class="flex w-80 flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Responsável:</span>
            <SelectInput
              :model-value="stages[stage.key].memberId ?? ''"
              :options="memberOptions(stage.key)"
              label="Selecione o responsável"
              :disabled="missingStages[stage.key]"
              :data-testid="`record-homologation-responsible-${stage.key}`"
              @update:model-value="onMemberChange(stage.key, $event)"
            />
          </div>
          <div class="flex w-80 flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Cargo:</span>
            <SelectInput
              :model-value="stages[stage.key].positionId ?? ''"
              :options="positionOptions(stage.key)"
              label="Selecione o cargo"
              :disabled="missingStages[stage.key] || stages[stage.key].memberId === undefined"
              :data-testid="`record-homologation-position-${stage.key}`"
              @update:model-value="onPositionChange(stage.key, $event)"
            />
          </div>
          <div class="flex flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Data:</span>
            <button
              type="button"
              class="flex h-11 items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4 text-sm text-gray-07 shadow-xs disabled:opacity-50"
              :disabled="missingStages[stage.key]"
              :data-testid="`record-homologation-date-${stage.key}`"
              @click="openPickers[stage.key] = true"
            >
              <IconImage
                :src="CalendarIcon"
                alt=""
              />
              {{ dateLabel(stageDate(stage.key)) }}
            </button>
            <DatePicker
              :selected-date="stageDate(stage.key)"
              :is-open="openPickers[stage.key] ?? false"
              @update:selected-date="setStageDate(stage.key, $event)"
              @update:is-open="openPickers[stage.key] = $event"
            />
          </div>
        </div>
        <!-- No decision row to patch, so the stage is locked instead of lying. -->
        <p
          v-if="missingStages[stage.key]"
          class="text-sm text-gray-05"
          :data-testid="`record-homologation-missing-${stage.key}`"
        >
          Esta etapa ainda não aconteceu para este registro.
        </p>
      </section>

      <section
        class="flex flex-col gap-2 p-6"
        data-testid="record-homologation-stage-validity"
      >
        <h2 class="text-[0.9375rem] font-semibold text-gray-07">
          Vigência
        </h2>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-gray-07">Data:</span>
          <button
            type="button"
            class="flex h-11 w-fit items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4 text-sm text-gray-07 shadow-xs"
            data-testid="record-homologation-date-validity"
            @click="openPickers.validity = true"
          >
            <IconImage
              :src="CalendarIcon"
              alt=""
            />
            {{ dateLabel(effectiveDateAsDate) }}
          </button>
          <DatePicker
            :selected-date="effectiveDateAsDate"
            :is-open="openPickers.validity ?? false"
            @update:selected-date="setEffectiveDate($event)"
            @update:is-open="openPickers.validity = $event"
          />
          <p
            v-if="effectiveDateError"
            class="text-sm text-red-500"
            data-testid="record-homologation-validity-error"
          >
            {{ effectiveDateError }}
          </p>
        </div>
      </section>
    </div>

    <div
      class="flex shrink-0 justify-end gap-6 border-t border-gray-02 px-6 py-8"
      data-testid="record-homologation-actions"
    >
      <ColoredButton
        outlined
        color="gray-07"
        border-color="gray-02"
        text="Cancelar"
        data-testid="record-homologation-cancel"
        @click="emit('close')"
      />
      <ColoredButton
        :icon="CheckMarkIcon"
        icon-alt=""
        text="Salvar"
        :disabled="!canSave"
        data-testid="record-homologation-save"
        @click="handleSave"
      />
    </div>
  </div>
</template>
